import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, fmtINR, fmtDateTime } from "@/lib/api";
import { Search, Filter, Download, Receipt, RefreshCw, CreditCard, UserPlus } from "lucide-react";
import MembershipCardModal from "@/components/MembershipCardModal";
import RegisterMembershipModal from "@/components/RegisterMembershipModal";

export default function AdminSales() {
  const [rows, setRows] = useState([]);
  const [agents, setAgents] = useState([]);
  const [q, setQ] = useState("");
  const [agentId, setAgentId] = useState("all");
  const [status, setStatus] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [cardOf, setCardOf] = useState(null);
  const [showRegister, setShowRegister] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  const load = async () => {
    setLoading(true);
    const params = { limit: 200 };
    if (q) params.q = q;
    if (agentId !== "all") params.agent_id = agentId;
    if (status === "paid") params.commission_paid = true;
    if (status === "pending") params.commission_paid = false;
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    try {
      const { data } = await api.get("/admin/memberships", { params });
      setRows(data);
      setPage(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/admin/agents").then((r) => setAgents(r.data));
    load();
    // eslint-disable-next-line
  }, []);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.revenue += r.amount || 0;
        acc.commission += r.commission_amount || 0;
        if (!r.commission_paid) acc.pending += r.commission_amount || 0;
        return acc;
      },
      { revenue: 0, commission: 0, pending: 0 }
    );
  }, [rows]);

  const exportCSV = () => {
    const head = [
      "Membership #",
      "Patient name",
      "Mobile",
      "Agent",
      "Sold on",
      "Amount",
      "Commission",
      "Status",
    ];
    const csv = [head.join(",")]
      .concat(
        rows.map((r) =>
          [
            r.membership_number,
            JSON.stringify(r.patient_name || ""),
            r.patient_mobile,
            r.agent_username || "walkin",
            JSON.stringify(fmtDateTime(r.issued_at)),
            r.amount,
            r.commission_amount,
            r.commission_paid ? "paid" : "pending",
          ].join(",")
        )
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `magicdil-sales-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-primary mb-1.5">
            All sales
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
            Every card, every agent
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setShowRegister(true)} className="bg-gradient-primary text-primary-foreground">
            <UserPlus className="h-4 w-4" /> Register membership
          </Button>
          <Button variant="outline" onClick={exportCSV} disabled={rows.length === 0}>
            <Download className="h-4 w-4" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-5">
        <div className="rounded-2xl border border-border/70 bg-card p-5">
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Cards (filtered)</div>
          <div className="mt-1 font-display text-2xl font-bold text-foreground">{rows.length}</div>
        </div>
        <div className="rounded-2xl border border-border/70 bg-card p-5">
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Revenue</div>
          <div className="mt-1 font-display text-2xl font-bold text-foreground">{fmtINR(totals.revenue)}</div>
        </div>
        <div className="rounded-2xl border border-border/70 bg-card p-5">
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Commission · Pending</div>
          <div className="mt-1 font-display text-2xl font-bold text-foreground">
            {fmtINR(totals.commission)}
            <span className="text-sm text-warning-foreground/90 font-medium ml-1">/ {fmtINR(totals.pending)}</span>
          </div>
        </div>
      </div>

      <Card className="border-border/70 shadow-soft overflow-hidden">
        <CardContent className="p-5 sm:p-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
            <div className="relative col-span-2 lg:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search name, mobile or membership #"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="h-10 pl-9"
              />
            </div>
            <Select value={agentId} onValueChange={setAgentId}>
              <SelectTrigger className="h-10">
                <SelectValue placeholder="Filter by agent" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All agents</SelectItem>
                <SelectItem value="0">Walk-in (no agent)</SelectItem>
                {agents.map((a) => (
                  <SelectItem key={a.agent_id} value={a.agent_id}>
                    {a.agent_name} (@{a.agent_username})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-10">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-10" />
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-10" />
          </div>

          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Filter className="h-3.5 w-3.5" /> Apply filters then search
            </div>
            <Button size="sm" onClick={load} disabled={loading} className="bg-gradient-primary text-primary-foreground">
              {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Apply
            </Button>
          </div>

          {rows.length === 0 ? (
            <div className="py-14 text-center">
              <Receipt className="h-10 w-10 mx-auto text-primary/60 mb-2" />
              <div className="font-semibold text-foreground">No sales match</div>
              <p className="text-sm text-muted-foreground mt-1">Try widening your filters or date range.</p>
            </div>
          ) : (
            <div className="overflow-x-auto -mx-5 sm:-mx-6">
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                  <tr className="border-b border-border">
                    <th className="text-left font-semibold px-5 sm:px-6 py-3">Patient</th>
                    <th className="text-left font-semibold py-3">Membership #</th>
                    <th className="text-left font-semibold py-3">Agent</th>
                    <th className="text-left font-semibold py-3">When</th>
                    <th className="text-left font-semibold py-3">Payment</th>
                    <th className="text-right font-semibold py-3">Sale</th>
                    <th className="text-right font-semibold py-3">Commission</th>
                    <th className="text-right font-semibold px-5 sm:px-6 py-3">Card</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {pageRows.map((m) => (
                    <tr key={m.id} className="hover:bg-muted/40">
                      <td className="px-5 sm:px-6 py-3">
                        <div className="font-medium text-foreground">{m.patient_name}</div>
                        <div className="text-xs text-muted-foreground">+91 {m.patient_mobile}</div>
                      </td>
                      <td className="py-3 font-mono-card text-xs tracking-wider text-foreground">
                        {m.membership_number}
                      </td>
                      <td className="py-3 text-muted-foreground">
                        {m.agent_name ? (
                          <span className="text-foreground">{m.agent_name}<span className="text-muted-foreground"> · @{m.agent_username}</span></span>
                        ) : <span className="italic">walk-in</span>}
                      </td>
                      <td className="py-3 text-muted-foreground">{fmtDateTime(m.issued_at)}</td>
                      <td className="py-3">
                        {m.payment_method === "admin_free" ? (
                          <span className="inline-flex items-center rounded-full bg-violet-500/10 text-violet-600 px-2 py-0.5 text-xs font-medium">Created by Admin</span>
                        ) : m.payment_method === "wallet" ? (
                          <span className="inline-flex items-center rounded-full bg-amber-500/10 text-amber-600 px-2 py-0.5 text-xs font-medium">Cash Payment</span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-primary/10 text-primary px-2 py-0.5 text-xs font-medium">Online Payment</span>
                        )}
                      </td>
                      <td className="py-3 text-right">{fmtINR(m.amount)}</td>
                      <td className="px-3 py-3 text-right">
                        <div className="font-semibold text-foreground">{fmtINR(m.commission_amount)}</div>
                        {m.commission_amount > 0 && (
                          <Badge
                            className={
                              "mt-1 text-[10px] " +
                              (m.commission_paid
                                ? "bg-success/12 text-success border border-success/25 hover:bg-success/12"
                                : "bg-warning/15 text-warning-foreground border border-warning/30 hover:bg-warning/15")
                            }
                          >
                            {m.commission_paid ? "Paid" : "Pending"}
                          </Badge>
                        )}
                      </td>
                      <td className="px-5 sm:px-6 py-3 text-right">
                        <Button variant="outline" size="sm" className="h-8" onClick={() => setCardOf(m)}>
                          <CreditCard className="h-3.5 w-3.5" /> View card
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {rows.length > PAGE_SIZE && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
              <span className="text-xs text-muted-foreground">
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, rows.length)} of {rows.length}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 px-3"
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  ←
                </Button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push("…");
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, idx) =>
                    p === "…" ? (
                      <span key={`ellipsis-${idx}`} className="px-1 text-xs text-muted-foreground">…</span>
                    ) : (
                      <Button
                        key={p}
                        variant={p === page ? "default" : "outline"}
                        size="sm"
                        className={`h-8 w-8 p-0 ${p === page ? "bg-gradient-primary text-primary-foreground" : ""}`}
                        onClick={() => setPage(p)}
                      >
                        {p}
                      </Button>
                    )
                  )}
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 px-3"
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  →
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      <MembershipCardModal
        open={!!cardOf}
        onOpenChange={() => setCardOf(null)}
        membership={cardOf}
      />
      <RegisterMembershipModal
        open={showRegister}
        onOpenChange={setShowRegister}
        agents={agents}
        onCreated={(m) => { load(); setCardOf(m); }}
      />
    </DashboardLayout>
  );
}
